/**
 * Driving the tonearm.
 *
 * The arm normally follows playback progress; dragging it, or a record swap,
 * takes it off automatic until the needle is cued again.
 */

import { armYawForProgress, LIFT_ANGLE, YAW_REST } from '../geometry/tonearm.ts';
import { ease } from '../lib/ease.ts';
import { clamp } from '../lib/math.ts';
import { type Tween, tween } from '../lib/tween.ts';
import { armSettleMs, type DeckState } from './deck-state.ts';
import type { Tonearm } from './parts/tonearm.ts';
import type { ProgressRing } from './progress-ring.ts';

/** How close to the rest the arm has to be to count as sitting on it. */
const AT_REST = 0.02;

export interface ArmControl {
  setArmDown(down: boolean, duration?: number): Tween;
  setProgress(p: number): void;
  dragArmTo(yaw: number): void;
  dropNeedle(): Promise<void>;
  liftNeedle(): Promise<void>;
  park(): void;
  restArm(): Promise<boolean>;
  cue(): void;
}

export function createArmControl(state: DeckState, arm: Tonearm, ring: ProgressRing): ArmControl {
  /** The lift under way, if one is. Only one may hold the needle. */
  let lifting: Tween | null = null;

  function setArmDown(down: boolean, duration = 420): Tween {
    state.armDown = down;
    /* A lift still in flight is abandoned first — two of them writing the same
       angle end with the needle wherever the slower one was going, which is
       how a quick lift-and-drop once left it in the air while playing. */
    lifting?.cancel();
    const mine = tween({
      from: arm.lift.rotation.z,
      to: down ? 0 : LIFT_ANGLE,
      duration,
      ease: ease.inOutCubic,
      onUpdate: (v) => {
        arm.lift.rotation.z = v;
      },
      onComplete: () => {
        if (lifting === mine) lifting = null;
      },
    });
    lifting = mine;
    return mine;
  }

  /** Send the arm to its rest — lifting it first if the needle is down, as a hand would. */
  function park(): void {
    state.armFollow = false;
    state.armTargetYaw = YAW_REST;
    if (state.armDown) void setArmDown(false, 200);
  }

  return {
    setArmDown,
    park,

    setProgress(p) {
      state.progress = clamp(p || 0, 0, 1);
      ring.setProgress(state.progress);
      /* A record mid-swap is not on the spindle, so the arm must not chase it. */
      if (state.armFollow && state.seated) {
        state.armTargetYaw = armYawForProgress(state.progress);
      }
    },

    /** Put the arm exactly where the hand holding it says — no easing while it is held. */
    dragArmTo(yaw) {
      state.armFollow = false;
      state.armTargetYaw = yaw;
      state.armYaw = yaw;
      arm.pivot.rotation.y = yaw;
    },

    /** Cue the arm down onto the groove; resolves once the needle has landed. */
    async dropNeedle() {
      state.armFollow = true;
      state.armTargetYaw = armYawForProgress(state.progress);
      /* Let the arm swing across before the needle comes down on it. */
      await tween({ duration: 420, ease: ease.inOutCubic });
      await setArmDown(true, 380);
    },

    async liftNeedle() {
      await setArmDown(false, 320);
      park();
    },

    /**
     * Carry the arm to its rest. Resolves true once it has settled there, or
     * false if something — a hand, a cue — took it elsewhere on the way.
     */
    async restArm() {
      park();
      await tween({ duration: Math.max(16, armSettleMs(state)) });
      return state.armTargetYaw === YAW_REST && Math.abs(state.armYaw - YAW_REST) < AT_REST;
    },

    cue() {
      state.armFollow = true;
      state.armTargetYaw = armYawForProgress(state.progress);
    },
  };
}
